<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Dispense Medications</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('hospital-dashboard')">Hospital</a></li>
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('pharmacy-dashboard')">Pharmacy</a></li>
                      <li class="breadcrumb-item active">Dispense</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary mb-3"></div>
              <p class="text-muted mb-0">Loading prescription...</p>
            </div>

            <div v-else class="row">
              <!-- Left Column: Patient & Prescription Info -->
              <div class="col-lg-4">
                <!-- Patient Card -->
                <div class="card">
                  <div class="card-body p-4">
                    <h6 class="mb-3"><i class="mdi mdi-account me-2 text-primary"></i>Patient Information</h6>
                    <div class="d-flex align-items-center mb-3">
                      <div class="avatar-md me-3">
                        <span class="avatar-title rounded-circle" :class="prescription.patientGender === 'Male' ? 'bg-primary' : 'bg-pink'" style="font-size: 1.2rem;">
                          {{ getInitials(prescription.patientName) }}
                        </span>
                      </div>
                      <div>
                        <h5 class="mb-1">{{ prescription.patientName }}</h5>
                        <span class="text-muted">{{ prescription.patientAge }} yrs</span>
                      </div>
                    </div>

                    <div class="row g-3">
                      <div class="col-6">
                        <small class="text-muted d-block">Patient ID</small>
                        <span>{{ prescription.patientID }}</span>
                      </div>
                      <div class="col-6">
                        <small class="text-muted d-block">Weight</small>
                        <span>{{ prescription.patientWeight || '-' }} kg</span>
                      </div>
                    </div>

                    <!-- Allergies Alert -->
                    <div v-if="prescription.allergies" class="alert alert-danger py-2 mt-3 mb-0">
                      <small><i class="mdi mdi-alert-circle me-1"></i><strong>Allergies:</strong> {{ prescription.allergies }}</small>
                    </div>
                  </div>
                </div>

                <!-- Prescription Details -->
                <div class="card">
                  <div class="card-body p-4">
                    <h6 class="mb-3"><i class="mdi mdi-prescription me-2 text-primary"></i>Prescription Details</h6>

                    <div class="mb-3">
                      <small class="text-muted d-block">Prescription ID</small>
                      <span class="fw-medium text-primary">{{ prescription.prescriptionID }}</span>
                    </div>

                    <div class="mb-3">
                      <small class="text-muted d-block">Diagnosis</small>
                      <span>{{ prescription.diagnosis || '-' }}</span>
                    </div>

                    <div class="row g-3">
                      <div class="col-6">
                        <small class="text-muted d-block">Prescriber</small>
                        <span>{{ prescription.prescriberName }}</span>
                      </div>
                      <div class="col-6">
                        <small class="text-muted d-block">Date</small>
                        <span>{{ formatDateTime(prescription.prescriptionDate) }}</span>
                      </div>
                    </div>

                    <div v-if="prescription.prescriptionNotes" class="mt-3">
                      <small class="text-muted d-block">Notes</small>
                      <span class="small">{{ prescription.prescriptionNotes }}</span>
                    </div>
                  </div>
                </div>

                <!-- Summary -->
                <div class="card bg-primary text-white">
                  <div class="card-body p-4">
                    <h6 class="mb-3 text-white"><i class="mdi mdi-calculator me-2"></i>Summary</h6>
                    <div class="d-flex justify-content-between mb-2">
                      <span>Total Items</span>
                      <span class="fw-bold">{{ items.length }}</span>
                    </div>
                    <div class="d-flex justify-content-between mb-2">
                      <span>Dispensed</span>
                      <span class="fw-bold">{{ dispensedCount }}</span>
                    </div>
                    <div class="d-flex justify-content-between mb-2">
                      <span>Pending</span>
                      <span class="fw-bold">{{ pendingCount }}</span>
                    </div>
                    <hr class="border-light">
                    <div class="d-flex justify-content-between">
                      <span>Total Amount</span>
                      <span class="fw-bold fs-5">KES {{ formatCurrency(totalAmount) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right Column: Dispensing Form -->
              <div class="col-lg-8">
                <div class="card">
                  <div class="card-body p-4">
                    <div class="d-flex justify-content-between align-items-center mb-4">
                      <div>
                        <h5 class="mb-1"><i class="mdi mdi-pill me-2 text-primary"></i>Medications</h5>
                        <small class="text-muted">Dispense medications to patient</small>
                      </div>
                      <span class="badge" :class="getStatusBadge(prescription.status)">{{ prescription.status }}</span>
                    </div>

                    <!-- Medication Items -->
                    <div class="medication-list">
                      <div v-for="(item) in items" :key="item.itemID" class="medication-item mb-3">
                        <div class="card border" :class="{'border-success': item.dispensed}">
                          <div class="card-body p-3">
                            <div class="row align-items-center">
                              <div class="col-md-5">
                                <div class="d-flex align-items-center">
                                  <div class="drug-icon me-3">
                                    <i class="mdi mdi-pill" :class="item.dispensed ? 'text-success' : 'text-primary'"></i>
                                  </div>
                                  <div>
                                    <h6 class="mb-1">{{ item.drugName }}</h6>
                                    <small class="text-muted">{{ item.strength }} • {{ item.drugForm }}</small>
                                  </div>
                                </div>
                              </div>
                              <div class="col-md-4">
                                <div class="row g-2">
                                  <div class="col-6">
                                    <small class="text-muted d-block">Dosage</small>
                                    <span class="small">{{ item.dosage }}</span>
                                  </div>
                                  <div class="col-6">
                                    <small class="text-muted d-block">Frequency</small>
                                    <span class="small">{{ item.frequency }}</span>
                                  </div>
                                  <div class="col-6">
                                    <small class="text-muted d-block">Duration</small>
                                    <span class="small">{{ item.duration }}</span>
                                  </div>
                                  <div class="col-6">
                                    <small class="text-muted d-block">Route</small>
                                    <span class="small">{{ item.route }}</span>
                                  </div>
                                </div>
                              </div>
                              <div class="col-md-3">
                                <div class="text-end">
                                  <div v-if="!item.dispensed">
                                    <div class="mb-2">
                                      <small class="text-muted">Prescribed: <strong>{{ item.quantity }}</strong></small>
                                    </div>
                                    <div class="input-group input-group-sm mb-2">
                                      <span class="input-group-text">Qty</span>
                                      <input type="number" class="form-control text-center"
                                             v-model.number="item.quantityToDispense"
                                             :max="item.quantity" min="0" style="max-width: 70px;">
                                    </div>
                                    <button class="btn btn-sm btn-success w-100"
                                            @click="dispenseItem(item)"
                                            :disabled="!item.quantityToDispense || item.quantityToDispense <= 0 || dispensingItem === item.itemID">
                                      <span v-if="dispensingItem === item.itemID">
                                        <i class="mdi mdi-loading mdi-spin"></i>
                                      </span>
                                      <span v-else>
                                        <i class="mdi mdi-check me-1"></i>Dispense
                                      </span>
                                    </button>
                                  </div>
                                  <div v-else class="text-center">
                                    <span class="badge bg-success mb-2">
                                      <i class="mdi mdi-check-circle me-1"></i>Dispensed
                                    </span>
                                    <div class="small text-muted">
                                      Qty: {{ item.quantityDispensed || item.quantity }}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <!-- Instructions -->
                            <div v-if="item.instructions" class="mt-2 pt-2 border-top">
                              <small class="text-muted"><i class="mdi mdi-information-outline me-1"></i>{{ item.instructions }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Counseling Notes -->
                    <div class="mt-4">
                      <label class="form-label">Counseling Notes</label>
                      <textarea class="form-control" rows="2" v-model="counselingNotes"
                                placeholder="Patient counseling notes..."></textarea>
                    </div>

                    <!-- Action Buttons -->
                    <div class="d-flex justify-content-between align-items-center pt-4 border-top mt-4">
                      <button type="button" class="btn btn-light" @click="goTo('pharmacy-dashboard')">
                        <i class="mdi mdi-arrow-left me-1"></i> Back to Queue
                      </button>
                      <div class="d-flex gap-2">
                        <button type="button" class="btn btn-outline-info" @click="printLabel">
                          <i class="mdi mdi-printer me-1"></i> Print Labels
                        </button>
                        <button v-if="allDispensed" type="button" class="btn btn-success" @click="completeDispensing" :disabled="completing">
                          <span v-if="completing"><i class="mdi mdi-loading mdi-spin me-1"></i> Processing...</span>
                          <span v-else><i class="mdi mdi-check-all me-1"></i> Complete & Close</span>
                        </button>
                        <button v-else type="button" class="btn btn-primary" @click="dispenseAll" :disabled="dispensingAll">
                          <span v-if="dispensingAll"><i class="mdi mdi-loading mdi-spin me-1"></i> Dispensing...</span>
                          <span v-else><i class="mdi mdi-pill me-1"></i> Dispense All</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">County Hospital Management System</div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <!-- Toast Notification -->
    <ToastNotification ref="toast" />
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ToastNotification from "@/components/hospital/ToastNotification.vue";
import { execute, hospital } from "@/api";

export default {
  name: "Dispensing",
  components: { NavigationBar, ToastNotification },
  data() {
    return {
      loading: true,
      dispensingItem: null,
      dispensingAll: false,
      completing: false,

      prescriptionID: '',
      prescription: {},
      items: [],
      counselingNotes: '',

      // Session
      facilityID: '',
      facilityName: '',
      sessionNames: '',
      sessionIdNo: '',
      pharmacistID: ''
    };
  },
  computed: {
    dispensedCount() {
      return this.items.filter(i => i.dispensed || i.status === 'Dispensed').length;
    },
    pendingCount() {
      return this.items.filter(i => !i.dispensed && i.status !== 'Dispensed').length;
    },
    allDispensed() {
      return this.items.length > 0 && this.items.every(i => i.dispensed || i.status === 'Dispensed');
    },
    totalAmount() {
      return this.items.reduce((sum, item) => sum + parseFloat(item.totalPrice || 0), 0);
    }
  },
  methods: {
    goTo(route) {
      this.$router.push('/' + route);
    },

    showSuccess(message) {
      if (this.$refs.toast) {
        this.$refs.toast.success(message);
      }
    },

    showError(message) {
      if (this.$refs.toast) {
        this.$refs.toast.error(message);
      } else {
        alert(message);
      }
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    },

    formatDateTime(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    },

    formatCurrency(amount) {
      return Number(amount || 0).toLocaleString();
    },

    getStatusBadge(status) {
      switch (status) {
        case 'Active':
        case 'Pending': return 'bg-warning text-dark';
        case 'Processing':
        case 'Partial': return 'bg-info';
        case 'Dispensed': return 'bg-success';
        default: return 'bg-secondary';
      }
    },

    loadPrescription() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getPrescription");
      data.append("prescriptionID", this.prescriptionID);

      execute(data, hospital)
          .then((res) => {
            this.loading = false;
            if (res.data.success && res.data.data.prescription) {
              this.prescription = res.data.data.prescription;
              // Items are returned together with prescription
              this.items = (res.data.data.items || []).map(item => ({
                ...item,
                quantityToDispense: item.quantity - (item.quantityDispensed || 0),
                dispensed: item.status === 'Dispensed'
              }));
            } else {
              this.showError('Prescription not found');
              this.goTo('pharmacy-dashboard');
            }
          })
          .catch((e) => {
            this.loading = false;
            console.error(e);
            this.showError('Error loading prescription');
          });
    },

    dispenseItem(item) {
      if (!item.quantityToDispense || item.quantityToDispense <= 0) {
        this.showError('Please enter quantity to dispense');
        return;
      }

      this.dispensingItem = item.itemID;

      const data = new FormData();
      data.append("function", "dispensePrescriptionItem");
      data.append("itemID", item.itemID);
      data.append("quantityDispensed", item.quantityToDispense);
      data.append("batchNo", item.batchNo || '');
      data.append("dispensedBy", this.sessionNames);
      data.append("dispensingNotes", item.dispensingNotes || '');
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            this.dispensingItem = null;
            if (res.data.success) {
              this.showSuccess(`${item.drugName} dispensed successfully`);
              // Reload to get updated data
              this.loadPrescription();
            } else {
              this.showError(res.data.message || 'Failed to dispense');
            }
          })
          .catch((e) => {
            this.dispensingItem = null;
            console.error(e);
            this.showError('Error dispensing medication');
          });
    },

    dispenseAll() {
      const pendingItems = this.items.filter(i => !i.dispensed && i.status !== 'Dispensed');
      if (pendingItems.length === 0) {
        this.showError('No items to dispense');
        return;
      }

      if (!confirm(`Dispense all ${pendingItems.length} pending items?`)) return;

      this.dispensingAll = true;

      // Dispense each item sequentially
      const dispenseNext = (index) => {
        if (index >= pendingItems.length) {
          this.dispensingAll = false;
          this.showSuccess('All items dispensed successfully');
          this.loadPrescription();
          return;
        }

        const item = pendingItems[index];
        const qtyToDispense = item.quantity - (item.quantityDispensed || 0);

        const data = new FormData();
        data.append("function", "dispensePrescriptionItem");
        data.append("itemID", item.itemID);
        data.append("quantityDispensed", qtyToDispense);
        data.append("batchNo", '');
        data.append("dispensedBy", this.sessionNames);
        data.append("dispensingNotes", '');
        data.append("sessionNames", this.sessionNames);
        data.append("sessionIdNo", this.sessionIdNo);

        execute(data, hospital)
            .then((res) => {
              if (res.data.success) {
                item.dispensed = true;
                item.quantityDispensed = qtyToDispense;
              }
              dispenseNext(index + 1);
            })
            .catch(() => {
              dispenseNext(index + 1);
            });
      };

      dispenseNext(0);
    },

    completeDispensing() {
      // The dispensePrescriptionItem API automatically updates prescription and visit status
      // when all items are dispensed, so we just need to redirect
      this.showSuccess('Prescription completed! Visit marked as completed.');
      setTimeout(() => {
        this.goTo('pharmacy-dashboard');
      }, 1500);
    },

    printLabel() {
      const labels = this.items.map((item, index) => `
        <div class="label" style="page-break-after: always; border: 1px solid #000; padding: 10px; margin: 10px; width: 300px;">
          <h4 style="margin: 0 0 5px 0;">${this.facilityName}</h4>
          <hr style="margin: 5px 0;">
          <p style="margin: 3px 0;"><strong>Patient:</strong> ${this.prescription.patientName}</p>
          <p style="margin: 3px 0;"><strong>Drug:</strong> ${item.drugName} ${item.strength}</p>
          <p style="margin: 3px 0;"><strong>Dosage:</strong> ${item.dosage} ${item.frequency}</p>
          <p style="margin: 3px 0;"><strong>Duration:</strong> ${item.duration}</p>
          <p style="margin: 3px 0;"><strong>Qty:</strong> ${item.quantity}</p>
          ${item.instructions ? `<p style="margin: 3px 0; font-size: 11px;"><strong>Note:</strong> ${item.instructions}</p>` : ''}
          <p style="margin: 8px 0 3px 0; font-size: 10px;">Date: ${new Date().toLocaleDateString()}</p>
        </div>
      `).join('');

      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
        <head>
          <title>Medication Labels</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 12px; }
            @media print { .label { page-break-after: always; } }
          </style>
        </head>
        <body>${labels}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  },
  mounted() {
    this.prescriptionID = this.$route.params.id;
    this.facilityID = sessionStorage.getItem("hospitalFacilityID") || '';
    this.facilityName = sessionStorage.getItem("hospitalFacilityName") || '';
    this.sessionNames = sessionStorage.getItem("sessionNames") || '';
    this.sessionIdNo = sessionStorage.getItem("sessionIdNo") || '';
    this.pharmacistID = sessionStorage.getItem("staffID") || sessionStorage.getItem("userID") || '';

    if (this.prescriptionID) {
      this.loadPrescription();
    } else {
      this.showError('No prescription specified');
      this.goTo('pharmacy-dashboard');
    }
  }
};
</script>

<style scoped>
.avatar-md { width: 4rem; height: 4rem; }
.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: 600;
  color: #fff;
}

.bg-pink { background-color: #e83e8c !important; }

.card {
  border: none;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

.drug-icon {
  width: 45px;
  height: 45px;
  background: #f8f9fa;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drug-icon i {
  font-size: 24px;
}

.medication-item .card {
  transition: all 0.3s ease;
}

.medication-item .card:hover {
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.medication-item .card.border-success {
  background: linear-gradient(135deg, #f0fff4 0%, #fff 100%);
}
</style>